// Arrays — curated interview problems with multiple worked approaches.
// Steps stay concise on purpose — the "visual" field on the key approach drives an
// interactive step-through so students SEE what changes after each step, instead of
// reading it in a paragraph.

const arraysProblems = [
  {
    id: 'best-time-to-buy-sell-stock',
    title: 'Best Time to Buy and Sell Stock',
    difficulty: 'Easy',
    leetcode: { number: 121, url: 'https://leetcode.com/problems/best-time-to-buy-and-sell-stock/' },
    statement:
      'Given an array prices where prices[i] is the stock price on day i, find the maximum profit ' +
      'from buying the stock on one day and selling it on a later day. If no profit is possible, return 0.',
    example: {
      input: 'prices = [7, 1, 5, 3, 6, 4]',
      output: '5',
      note: 'Buy on day 2 at price 1, sell on day 5 at price 6 — profit = 6 − 1 = 5.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Pair)',
        why:
          "\"Profit\" here just means sell price minus buy price, and you're only allowed to sell on a day " +
          "AFTER you bought — never before. The most direct way to guarantee you find the best possible profit " +
          "is to literally try every valid (buy day, sell day) combination and keep whichever gives the biggest " +
          "number. It's slow, but it's impossible to get wrong, which makes it the right place to start.",
        steps: [
          'Fix a buy day i, for every index in the array.',
          'Try every later day j > i as a possible sell day.',
          'Compute profit = prices[j] − prices[i], keep the running best.',
          'Every (buy, sell) pair is checked once — that\'s the O(n²).',
        ],
        visual: {
          arrays: [{ label: 'prices', values: [7, 1, 5, 3, 6, 4] }],
          varNames: ['maxProfit'],
          steps: [
            { highlights: { prices: { primary: [0], secondary: [1] } }, vars: { maxProfit: 0 }, note: 'Buy day 0 (price 7), sell day 1 (price 1): profit = -6. Not better than 0.' },
            { highlights: { prices: { primary: [0], secondary: [2] } }, vars: { maxProfit: 0 }, note: 'Buy day 0 (7), sell day 2 (5): profit = -2. Still no.' },
            { highlights: { prices: { primary: [0], secondary: [3] } }, vars: { maxProfit: 0 }, note: 'Buy day 0 (7), sell day 3 (3): profit = -4. No.' },
            { highlights: { prices: { primary: [0], secondary: [4] } }, vars: { maxProfit: 0 }, note: 'Buy day 0 (7), sell day 4 (6): profit = -1. No.' },
            { highlights: { prices: { primary: [0], secondary: [5] } }, vars: { maxProfit: 0 }, note: 'Buy day 0 (7), sell day 5 (4): profit = -3. Buying on day 0 never wins — it\'s the highest price in the array.' },
            { highlights: { prices: { primary: [1], secondary: [2] } }, vars: { maxProfit: 4 }, note: 'Buy day 1 (1), sell day 2 (5): profit = 4. First real profit — new best!' },
            { highlights: { prices: { primary: [1], secondary: [3] } }, vars: { maxProfit: 4 }, note: 'Buy day 1 (1), sell day 3 (3): profit = 2. Not better than 4.' },
            { highlights: { prices: { primary: [1], secondary: [4] } }, vars: { maxProfit: 5 }, note: 'Buy day 1 (1), sell day 4 (6): profit = 5. New best!' },
            { highlights: { prices: { primary: [1], secondary: [5] } }, vars: { maxProfit: 5 }, note: 'Buy day 1 (1), sell day 5 (4): profit = 3. Not better than 5.' },
            { highlights: { prices: { primary: [2], secondary: [3] } }, vars: { maxProfit: 5 }, note: 'Buy day 2 (5), sell day 3 (3): profit = -2. No.' },
            { highlights: { prices: { primary: [2], secondary: [4] } }, vars: { maxProfit: 5 }, note: 'Buy day 2 (5), sell day 4 (6): profit = 1. Not better than 5.' },
            { highlights: { prices: { primary: [2], secondary: [5] } }, vars: { maxProfit: 5 }, note: 'Buy day 2 (5), sell day 5 (4): profit = -1. No.' },
            { highlights: { prices: { primary: [3], secondary: [4] } }, vars: { maxProfit: 5 }, note: 'Buy day 3 (3), sell day 4 (6): profit = 3. Not better than 5.' },
            { highlights: { prices: { primary: [3], secondary: [5] } }, vars: { maxProfit: 5 }, note: 'Buy day 3 (3), sell day 5 (4): profit = 1. No.' },
            { highlights: { prices: { primary: [4], secondary: [5] } }, vars: { maxProfit: 5 }, note: 'Buy day 4 (6), sell day 5 (4): profit = -2. All 15 pairs checked — final answer stays 5. That\'s n(n−1)/2 = 15 comparisons for just 6 days — the O(n²) cost, visibly.' },
          ],
        },
        code:
`public int maxProfitBruteForce(int[] prices) {
    int maxProfit = 0;
    for (int i = 0; i < prices.length; i++) {
        for (int j = i + 1; j < prices.length; j++) {
            int profit = prices[j] - prices[i];
            if (profit > maxProfit) {
                maxProfit = profit;
            }
        }
    }
    return maxProfit;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse:
          "Say this out loud first to confirm you understand the constraint, then flag that it's O(n²) and move " +
          "to the optimized version — never let it be your final answer.",
      },
      {
        name: 'Approach 2 — Optimized: Single Pass, Track the Minimum So Far',
        why:
          "Look back at the brute force: for sell day 4, we compared against EVERY earlier buy day, but only the " +
          "cheapest one (day 1, price 1) could ever win — comparing against days 0, 2, or 3 was wasted effort, " +
          "since none of them could beat the cheapest price already seen. If you just remember the cheapest price " +
          "seen so far as you walk forward, you get the same answer in one pass instead of rescanning backward " +
          "every single day.",
        steps: [
          'Start minPrice at prices[0] and maxProfit at 0.',
          'Walk the array left to right, one day at a time.',
          'If today is a new low, update minPrice.',
          'Otherwise, check if selling today beats maxProfit, and update it if so.',
        ],
        visual: {
          arrays: [{ label: 'prices', values: [7, 1, 5, 3, 6, 4] }],
          varNames: ['minPrice', 'maxProfit'],
          steps: [
            { highlights: { prices: [0] }, vars: { minPrice: 7, maxProfit: 0 }, note: 'Start: minPrice = prices[0] = 7.' },
            { highlights: { prices: [1] }, vars: { minPrice: 1, maxProfit: 0 }, note: '1 < 7 → new low. minPrice updates to 1.' },
            { highlights: { prices: [2] }, vars: { minPrice: 1, maxProfit: 4 }, note: 'Sell at 5 vs minPrice 1 → profit 4. New best.' },
            { highlights: { prices: [3] }, vars: { minPrice: 1, maxProfit: 4 }, note: 'Sell at 3 vs minPrice 1 → profit 2. Not better, skip.' },
            { highlights: { prices: [4] }, vars: { minPrice: 1, maxProfit: 5 }, note: 'Sell at 6 vs minPrice 1 → profit 5. New best.' },
            { highlights: { prices: [5] }, vars: { minPrice: 1, maxProfit: 5 }, note: 'Sell at 4 vs minPrice 1 → profit 3. Not better. Final answer: 5.' },
          ],
        },
        code:
`public int maxProfit(int[] prices) {
    int minPrice = prices[0];
    int maxProfit = 0;

    for (int i = 1; i < prices.length; i++) {
        if (prices[i] < minPrice) {
            minPrice = prices[i];
        } else if (prices[i] - minPrice > maxProfit) {
            maxProfit = prices[i] - minPrice;
        }
    }
    return maxProfit;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "This running-best-in-one-pass idea reappears almost identically in Kadane's Algorithm below.",
        whenToUse:
          "Use this whenever the answer is \"the best decision achievable today, given only what happened before " +
          "today\" — one running piece of state, updated once per element, in a single forward sweep.",
      },
    ],
  },
  {
    id: 'product-of-array-except-self',
    title: 'Product of Array Except Self',
    difficulty: 'Medium',
    leetcode: { number: 238, url: 'https://leetcode.com/problems/product-of-array-except-self/' },
    statement:
      'Given an integer array nums, return an array output where output[i] equals the product of every ' +
      'element in nums except nums[i] — without using the division operator, in O(n) time.',
    example: {
      input: 'nums = [1, 2, 3, 4]',
      output: '[24, 12, 8, 6]',
      note: 'output[0] = 2×3×4 = 24, output[1] = 1×3×4 = 12, and so on.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Recompute Every Product)',
        why:
          "\"Except self\" just means: multiply every number in the array together, except skip the one at the " +
          "position you're currently filling in. The most direct way to do that for EVERY position is to run a " +
          "full inner loop for each one, multiplying in everything except the current index. Correct, but watch " +
          "what happens for neighboring positions — output[0] and output[1] both end up multiplying nums[2] and " +
          "nums[3] again from scratch, redoing work that was already done one step earlier.",
        steps: [
          'For each index i, start a running product at 1.',
          'Multiply in every nums[j] where j ≠ i.',
          'Store the result in output[i].',
          'Repeating the full inner scan for every i gives O(n²).',
        ],
        visual: {
          arrays: [
            { label: 'nums', values: [1, 2, 3, 4] },
            { label: 'output', values: [null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { nums: { primary: [1, 2, 3] }, output: { secondary: [0] } }, snapshots: { output: [24, null, null, null] }, note: 'i = 0: multiply nums[1] × nums[2] × nums[3] = 2 × 3 × 4 = 24. nums[0] itself is skipped.' },
            { highlights: { nums: { primary: [0, 2, 3] }, output: { secondary: [1] } }, snapshots: { output: [24, 12, null, null] }, note: 'i = 1: multiply nums[0] × nums[2] × nums[3] = 1 × 3 × 4 = 12. Notice nums[2] and nums[3] just got multiplied again.' },
            { highlights: { nums: { primary: [0, 1, 3] }, output: { secondary: [2] } }, snapshots: { output: [24, 12, 8, null] }, note: 'i = 2: multiply nums[0] × nums[1] × nums[3] = 1 × 2 × 4 = 8. Same repeated work, different skipped index.' },
            { highlights: { nums: { primary: [0, 1, 2] }, output: { secondary: [3] } }, snapshots: { output: [24, 12, 8, 6] }, note: 'i = 3: multiply nums[0] × nums[1] × nums[2] = 1 × 2 × 3 = 6. Four full inner scans for four positions — that repeated multiplication is exactly the O(n²) cost the optimized approach below eliminates.' },
          ],
        },
        code:
`public int[] productExceptSelfBruteForce(int[] nums) {
    int n = nums.length;
    int[] output = new int[n];

    for (int i = 0; i < n; i++) {
        int product = 1;
        for (int j = 0; j < n; j++) {
            if (j != i) {
                product *= nums[j];
            }
        }
        output[i] = product;
    }
    return output;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        whenToUse: "State this only to confirm you understand \"except self\" — then move to the optimized version.",
      },
      {
        name: 'Approach 2 — Division Shortcut (and why it\'s disqualified)',
        why:
          "Multiply everything once, then divide out nums[i] per position. Fast, but the problem bans division " +
          "and it breaks the moment any element is 0.",
        steps: [
          'Multiply all non-zero elements into totalProduct; count zeros separately.',
          'Two or more zeros → every output is 0.',
          'Exactly one zero → only that position gets totalProduct, everything else is 0.',
          'No zeros → output[i] = totalProduct / nums[i].',
        ],
        code:
`// Not allowed by the problem's constraints (uses division) — shown to explain why it's rejected
public int[] productExceptSelfDivision(int[] nums) {
    int totalProduct = 1, zeroCount = 0;
    for (int num : nums) {
        if (num == 0) zeroCount++;
        else totalProduct *= num;
    }

    int[] output = new int[nums.length];
    for (int i = 0; i < nums.length; i++) {
        if (zeroCount > 1) output[i] = 0;
        else if (zeroCount == 1) output[i] = (nums[i] == 0) ? totalProduct : 0;
        else output[i] = totalProduct / nums[i];
    }
    return output;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra' },
        whenToUse:
          "Only mention this to show you saw it and can explain why it fails here — never use it as the final " +
          "answer for this specific problem.",
      },
      {
        name: 'Approach 3 — Optimized: Prefix & Suffix Products (No Division)',
        why:
          "output[i] = (everything to its left) × (everything to its right). Build the left products in one " +
          "pass, then fold in the right products in a second pass — no division needed.",
        steps: [
          'output[0] = 1 — nothing sits to its left.',
          'Forward pass: output[i] = output[i-1] × nums[i-1] for each i onward.',
          'Backward pass: keep a running suffix, starting at 1.',
          'At each i, multiply output[i] by suffix, then update suffix ×= nums[i].',
        ],
        visual: {
          arrays: [
            { label: 'nums', values: [1, 2, 3, 4] },
            { label: 'output', values: [null, null, null, null] },
          ],
          varNames: ['suffix'],
          steps: [
            { highlights: { output: [0] }, snapshots: { output: [1, null, null, null] }, note: 'output[0] = 1 — nothing to the left.', phase: 'Prefix' },
            { highlights: { output: [1], nums: [0] }, snapshots: { output: [1, 1, null, null] }, note: 'output[1] = output[0] × nums[0] = 1 × 1 = 1.', phase: 'Prefix' },
            { highlights: { output: [2], nums: [1] }, snapshots: { output: [1, 1, 2, null] }, note: 'output[2] = output[1] × nums[1] = 1 × 2 = 2.', phase: 'Prefix' },
            { highlights: { output: [3], nums: [2] }, snapshots: { output: [1, 1, 2, 6] }, note: 'output[3] = output[2] × nums[2] = 2 × 3 = 6. Prefix pass done.', phase: 'Prefix' },
            { highlights: { output: [3] }, snapshots: { output: [1, 1, 2, 6] }, vars: { suffix: 4 }, note: 'Combine output[3] with suffix (1) → 6. suffix updates to 4 (×nums[3]).', phase: 'Suffix' },
            { highlights: { output: [2] }, snapshots: { output: [1, 1, 8, 6] }, vars: { suffix: 12 }, note: 'Combine output[2] with suffix (4) → 8. suffix updates to 12 (×nums[2]).', phase: 'Suffix' },
            { highlights: { output: [1] }, snapshots: { output: [1, 12, 8, 6] }, vars: { suffix: 24 }, note: 'Combine output[1] with suffix (12) → 12. suffix updates to 24 (×nums[1]).', phase: 'Suffix' },
            { highlights: { output: [0] }, snapshots: { output: [24, 12, 8, 6] }, vars: { suffix: 24 }, note: 'Combine output[0] with suffix (24) → 24. Final: [24, 12, 8, 6].', phase: 'Suffix' },
          ],
        },
        code:
`public int[] productExceptSelf(int[] nums) {
    int n = nums.length;
    int[] output = new int[n];

    output[0] = 1;
    for (int i = 1; i < n; i++) {
        output[i] = output[i - 1] * nums[i - 1];   // prefix product up to i
    }

    int suffix = 1;
    for (int i = n - 1; i >= 0; i--) {
        output[i] *= suffix;                       // combine with suffix product
        suffix *= nums[i];
    }
    return output;
}`,
        complexity: { time: 'O(n)', space: 'O(1) extra (output array is the required result, not counted)' },
        whenToUse:
          "Reach for this whenever a value at position i depends on an aggregate of everything except i, and " +
          "you can't just divide the total back out. The same two-pass shape works for running sums and running " +
          "max/min, not just products.",
      },
    ],
  },
  {
    id: 'maximum-subarray',
    title: 'Maximum Subarray',
    difficulty: 'Medium',
    leetcode: { number: 53, url: 'https://leetcode.com/problems/maximum-subarray/' },
    statement:
      'Given an integer array nums, find the contiguous subarray (containing at least one number) with the ' +
      'largest possible sum, and return that sum.',
    example: {
      input: 'nums = [-2, 1, -3, 4, -1, 2, 1, -5, 4]',
      output: '6',
      note: 'The subarray [4, -1, 2, 1] sums to 6, which beats every other contiguous run.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Sum Every Subarray)',
        why:
          "\"Contiguous subarray\" means a run of elements sitting next to each other in the array — not any " +
          "elements you can pick and choose, only a connected stretch. So the most direct way to find the best " +
          "one is to try every possible stretch: pick a start, then grow it one element at a time toward the " +
          "end, checking the sum at every length along the way, and remember the biggest sum seen across every " +
          "stretch tried.",
        steps: [
          'Start maxSum at nums[0], not 0 — the subarray must have at least one element.',
          'Fix a start index i, try every index as a start.',
          'Extend a running sum to every end index j ≥ i.',
          'Compare against maxSum after every extension — O(n²) subarrays total.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [-2, 1, -3, 4, -1, 2, 1, -5, 4] }],
          varNames: ['bestFromHere', 'maxSum'],
          steps: [
            { highlights: { nums: { primary: [0] } }, vars: { bestFromHere: 2, maxSum: 2 }, note: 'Try every stretch starting at index 0. The best one found sums to 2. First candidate — new global best: 2.' },
            { highlights: { nums: { primary: [1] } }, vars: { bestFromHere: 4, maxSum: 4 }, note: 'Every stretch starting at index 1 checked. Best sums to 4 — new global best: 4.' },
            { highlights: { nums: { primary: [2] } }, vars: { bestFromHere: 3, maxSum: 4 }, note: 'Starting at index 2, the best stretch sums to 3 — not better than 4.' },
            { highlights: { nums: { primary: [3, 4, 5, 6] } }, vars: { bestFromHere: 6, maxSum: 6 }, note: 'Starting at index 3, the stretch [4, -1, 2, 1] sums to 6 — new global best: 6. This is the winning subarray.' },
            { highlights: { nums: { primary: [4] } }, vars: { bestFromHere: 2, maxSum: 6 }, note: 'Starting at index 4, best sums to 2 — not better than 6.' },
            { highlights: { nums: { primary: [5] } }, vars: { bestFromHere: 3, maxSum: 6 }, note: 'Starting at index 5, best sums to 3 — not better.' },
            { highlights: { nums: { primary: [6] } }, vars: { bestFromHere: 1, maxSum: 6 }, note: 'Starting at index 6, best sums to 1 — not better.' },
            { highlights: { nums: { primary: [7] } }, vars: { bestFromHere: -1, maxSum: 6 }, note: 'Starting at index 7, best sums to -1 — not better.' },
            { highlights: { nums: { primary: [8] } }, vars: { bestFromHere: 4, maxSum: 6 }, note: 'Starting at index 8 (the last element), best is just 4 — not better. Every one of the 45 possible stretches has now been checked. Final answer: 6.' },
          ],
        },
        code:
`public int maxSubArrayBruteForce(int[] nums) {
    int maxSum = nums[0];
    for (int i = 0; i < nums.length; i++) {
        int sum = 0;
        for (int j = i; j < nums.length; j++) {
            sum += nums[j];
            maxSum = Math.max(maxSum, sum);
        }
    }
    return maxSum;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse: "Only useful as a spoken warm-up to confirm \"contiguous\" — abandon it once complexity comes up.",
      },
      {
        name: "Approach 2 — Optimized: Kadane's Algorithm (Extend or Restart)",
        why:
          "Notice something in the brute-force walkthrough: the winning stretch started fresh at index 3, right " +
          "after index 2 (-3) dragged the running sum down. That's the whole idea here — instead of trying every " +
          "possible starting point separately, walk through once and, at each element, ask a single question: " +
          "does adding this element to the subarray I'm already building help, or would I be better off abandoning " +
          "that subarray and starting fresh right here? Carrying a negative running sum forward can only drag " +
          "future sums down, so the moment it stops helping, you drop it.",
        steps: [
          'Start currentSum and maxSum both at nums[0].',
          'At each element: currentSum = max(nums[i], currentSum + nums[i]).',
          'Extend if adding helps; restart fresh at nums[i] if it doesn\'t.',
          'Update maxSum whenever currentSum beats it.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [-2, 1, -3, 4, -1, 2, 1, -5, 4] }],
          varNames: ['currentSum', 'maxSum'],
          steps: [
            { highlights: { nums: [0] }, vars: { currentSum: -2, maxSum: -2 }, note: 'Start: currentSum = maxSum = nums[0] = -2.' },
            { highlights: { nums: [1] }, vars: { currentSum: 1, maxSum: 1 }, note: '-2+1=-1 vs 1 → restart at 1. New best: 1.' },
            { highlights: { nums: [2] }, vars: { currentSum: -2, maxSum: 1 }, note: '1-3=-2 vs -3 → extend (-2). Not a new best.' },
            { highlights: { nums: [3] }, vars: { currentSum: 4, maxSum: 4 }, note: '-2+4=2 vs 4 → restart at 4. New best: 4.' },
            { highlights: { nums: [4] }, vars: { currentSum: 3, maxSum: 4 }, note: '4-1=3 vs -1 → extend (3). Not a new best.' },
            { highlights: { nums: [5] }, vars: { currentSum: 5, maxSum: 5 }, note: '3+2=5 vs 2 → extend (5). New best: 5.' },
            { highlights: { nums: [6] }, vars: { currentSum: 6, maxSum: 6 }, note: '5+1=6 vs 1 → extend (6). New best: 6.' },
            { highlights: { nums: [7] }, vars: { currentSum: 1, maxSum: 6 }, note: '6-5=1 vs -5 → extend (1). Not a new best.' },
            { highlights: { nums: [8] }, vars: { currentSum: 5, maxSum: 6 }, note: '1+4=5 vs 4 → extend (5). Final answer: 6.' },
          ],
        },
        code:
`public int maxSubArray(int[] nums) {
    int currentSum = nums[0];
    int maxSum = nums[0];

    for (int i = 1; i < nums.length; i++) {
        currentSum = Math.max(nums[i], currentSum + nums[i]);
        maxSum = Math.max(maxSum, currentSum);
    }
    return maxSum;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Kadane's Algorithm is a 1-D dynamic programming idea wearing an array-traversal disguise.",
        whenToUse:
          "Reach for Kadane's whenever the best answer ending at position i only needs the answer ending at " +
          "i−1 plus the current element — no need to look further back.",
      },
    ],
  },
];

export default arraysProblems;
